import { Store } from '@toeverything/infra';

import type { FetchService } from '../services/fetch';

export type UserAiMode = 'standard' | 'user-keys';

export interface ModeResponse {
  mode: UserAiMode;
}

export interface KeysStatusResponse {
  openaiKeySet: boolean;
  openaiKeyMasked?: string | null; // First 4 + last 3 chars (e.g., "sk-p***xyz")
  claudeTokenSet: boolean;
  claudeTokenExpiresAt: string | null;
  webSearchKeySet: boolean;
  webSearchKeyMasked?: string | null; // First 4 + last 3 chars (e.g., "tvly***abc")
}

export interface ClaudeTokenStatusResponse {
  isSet: boolean;
  expiresAt: string | null;
  isExpired: boolean;
}

export interface OAuthInitResponse {
  authUrl: string;
  state: string;
}

export interface OAuthExchangeResponse {
  success: boolean;
  expiresAt: string;
}

export interface RoutingScenario {
  name: string;
  scenario: string;
  provider: string;
  keySource: 'user' | 'server';
  status: 'active' | 'unavailable';
}

export interface RoutingMatrixResponse {
  mode: UserAiMode;
  scenarios: RoutingScenario[];
}

/**
 * Store for User API Keys Mode API calls.
 *
 * Endpoints:
 * - GET/PUT /api/copilot/user-keys/mode
 * - GET /api/copilot/user-keys/keys
 * - PUT/DELETE /api/copilot/user-keys/keys/openai
 * - GET /api/copilot/user-keys/token/status
 */
export class UserApiKeysModeStore extends Store {
  constructor(private readonly fetchService: FetchService) {
    super();
  }

  async fetchMode(signal?: AbortSignal): Promise<ModeResponse> {
    const res = await this.fetchService.fetch('/api/copilot/user-keys/mode', {
      signal,
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch mode: ${res.statusText}`);
    }

    return res.json();
  }

  async setMode(mode: UserAiMode, signal?: AbortSignal): Promise<ModeResponse> {
    const res = await this.fetchService.fetch('/api/copilot/user-keys/mode', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
      signal,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(error.message || `Failed to set mode: ${res.statusText}`);
    }

    return res.json();
  }

  async fetchKeysStatus(signal?: AbortSignal): Promise<KeysStatusResponse> {
    const res = await this.fetchService.fetch('/api/copilot/user-keys/keys', {
      signal,
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch keys status: ${res.statusText}`);
    }

    // Backend returns: { keys: { openai: "***xyz", search: "***abc" } }
    // Transform to expected format
    const data = await res.json();
    const keys = data.keys || {};

    return {
      openaiKeySet: !!keys.openai,
      openaiKeyMasked: keys.openai || null,
      claudeTokenSet: !!keys.anthropic, // Claude uses anthropic provider
      claudeTokenExpiresAt: null, // Not tracked in simple key storage
      webSearchKeySet: !!keys.search,
      webSearchKeyMasked: keys.search || null,
    };
  }

  async setOpenAiKey(
    key: string,
    signal?: AbortSignal
  ): Promise<{ success: boolean }> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/keys/openai',
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
        signal,
      }
    );

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(
        error.message || `Failed to set OpenAI key: ${res.statusText}`
      );
    }

    return res.json();
  }

  async deleteOpenAiKey(signal?: AbortSignal): Promise<void> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/keys/openai',
      {
        method: 'DELETE',
        signal,
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to delete OpenAI key: ${res.statusText}`);
    }
  }

  async setWebSearchKey(
    key: string,
    signal?: AbortSignal
  ): Promise<{ success: boolean }> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/keys/search',
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
        signal,
      }
    );

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(
        error.message || `Failed to set web search key: ${res.statusText}`
      );
    }

    return res.json();
  }

  async deleteWebSearchKey(signal?: AbortSignal): Promise<void> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/keys/search',
      {
        method: 'DELETE',
        signal,
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to delete web search key: ${res.statusText}`);
    }
  }

  async fetchClaudeTokenStatus(
    signal?: AbortSignal
  ): Promise<ClaudeTokenStatusResponse> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/token/status',
      { signal }
    );

    if (!res.ok) {
      throw new Error(`Failed to fetch Claude token status: ${res.statusText}`);
    }

    return res.json();
  }

  async initOAuth(signal?: AbortSignal): Promise<OAuthInitResponse> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/oauth/init',
      { signal }
    );

    if (!res.ok) {
      throw new Error(`Failed to init OAuth: ${res.statusText}`);
    }

    return res.json();
  }

  async exchangeOAuthCode(
    code: string,
    state: string,
    signal?: AbortSignal
  ): Promise<OAuthExchangeResponse> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/oauth/exchange',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
        signal,
      }
    );

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(
        error.message || `Failed to exchange OAuth code: ${res.statusText}`
      );
    }

    return res.json();
  }

  async fetchRoutingMatrix(
    signal?: AbortSignal
  ): Promise<RoutingMatrixResponse> {
    const res = await this.fetchService.fetch(
      '/api/copilot/user-keys/routing-matrix',
      { signal }
    );

    if (!res.ok) {
      throw new Error(`Failed to fetch routing matrix: ${res.statusText}`);
    }

    return res.json();
  }
}
